import { Stack as MuiStack, type StackProps, Typography, type TypographyProps } from "~/common/mui";
import { useTranslation } from "react-i18next";
import {
  type NetPriceData,
  type PriceData,
  type TotalPriceData,
  type WithSurcharge,
} from "~/domains/pricing/types/PriceData";
import { formatCurrency } from "~/common/utils/currencyFormatter";
import { isNonNull } from "~/common/utils/reducers";

export type PriceProps = {
  price: PriceData | (PriceData & WithSurcharge) | undefined | null;
  surchargeEnabled?: boolean;
  tariffSurcharge?: boolean;
  zeroText?: string;
} & TypographyProps;

function PriceV4({ price, surchargeEnabled, tariffSurcharge = false, zeroText = "-", ...rest }: PriceProps) {
  let text;
  if (!price || !price.value) {
    text = zeroText;
  } else {
    const hasSurcharge = surchargeEnabled && "surcharge" in price && !!price.surcharge;

    let val: number;
    if (hasSurcharge) {
      val = price.value;
    } else {
      val = "baseValue" in price ? price.baseValue : price.value;
    }
    text = formatCurrency(val, price.currencyIso);

    if (hasSurcharge || tariffSurcharge) {
      text += "*";
    }
  }
  return (
    <Typography data-test-id={"value"} {...rest}>
      {text}
    </Typography>
  );
}

type NetPriceUnitProps = {
  price: NetPriceData | null | undefined;
  labelVariant?: "full" | "short";
} & TypographyProps;

const NetPriceLabel = ({ price, labelVariant = "full", ...rest }: NetPriceUnitProps) => {
  const { t } = useTranslation();
  if (!price) {
    return "";
  }

  const parts = labelVariant === "full" ? [t("cartPage.table.header.price.title")] : [];
  if (price.isSubscription) {
    parts.push(t("common.product.perMonth"));
  } else {
    parts.push(t("common.product.perPiece", { count: price.unit }));
  }

  return (
    <Typography data-test-id={"unit"} color="text.label" {...rest}>
      {parts.join(" ")}
    </Typography>
  );
};

type TotalPriceLabelProps = {
  price: TotalPriceData | undefined | null;
  labelVariant?: "full" | "short";
} & TypographyProps;

const TotalPriceLabel = ({ labelVariant = "full", price, ...rest }: TotalPriceLabelProps) => {
  const { t } = useTranslation();

  const label = t("common.product.totalValue");
  const unit = price?.isSubscription ? t("common.product.perYear") : undefined;

  return (
    <Typography data-test-id={"unit"} color="text.label" {...rest}>
      {labelVariant === "full" ? [label, unit].filter(isNonNull).join(" ") : label}
    </Typography>
  );
};

function Stack({ gap = 0.5, ...rest }: StackProps) {
  return <MuiStack gap={gap} {...rest} />;
}

type ListPriceLabelProps = Omit<TypographyProps, "children"> & {
  unit: number;
};

function ListPriceLabel({ unit, color = "text.label", ...rest }: ListPriceLabelProps) {
  const { t } = useTranslation();

  return (
    <Typography color={color} {...rest}>
      {t("addProduct.salesman.listPricePerPiece", { count: unit })}
    </Typography>
  );
}

export const PriceElements = {
  Price: PriceV4,
  NetPriceLabel,
  TotalPriceLabel,
  Stack,
  ListPriceLabel,
};
